import { type ReactNode } from 'react';

import { isDefined, isTruthy } from './isDefined.js';
import { type HelpTooltipProps } from '../components/Tooltips/Help.js';

export { isDefined, isTruthy } from './isDefined.js';
export { isDefinedObject } from './isDefinedObject.js';

export function assertNever(message: string, obj: never): never {
  void obj; // to avoid eslint errors
  throw new Error(message);
}

export function assert(condition: unknown, message?: string): asserts condition {
  if (!condition) {
    throw new Error(message || 'Assertion failure');
  }
}

export function isEnumMember<T extends object>(enumObj: T, value: unknown): value is T[keyof T] {
  return Object.values(enumObj).includes(value);
}

export function parseValue<T>(value: string): T | null {
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

export function parseJsonSafely(jsonString: string): {
  parsedJson: Record<string, any> | null;
  error: string | null;
} {
  try {
    const parsed = JSON.parse(jsonString);
    return { parsedJson: parsed, error: null };
  } catch (err) {
    const errorMessage =
      err instanceof SyntaxError ? `Invalid JSON: ${err.message}` : 'Invalid JSON format';
    return { parsedJson: null, error: errorMessage };
  }
}

export function hasValidMultiEnumWithOther<T>(props: {
  items: Record<string, any>[];
  valueKey: string;
  rawValueKey: string;
  otherValue: T;
}): boolean {
  const { items, valueKey, rawValueKey, otherValue } = props;
  return items.every(
    (item) =>
      isTruthy(item[valueKey]) && (item[valueKey] !== otherValue || isTruthy(item[rawValueKey]))
  );
}

/**
 * Requires either a `label` or an `aria-label` so a field can't ship unlabeled. A non-string
 * `label` carries no accessible name on its own, so it additionally requires `aria-label`.
 * See also:
 * https://notable-health.atlassian.net/wiki/spaces/notableengineering/pages/2552397847/Accessibility+Guidelines+For+Engineers
 */
export type RequireLabelOrAria =
  | { label: ReactNode; 'aria-label': string }
  | { label: string; 'aria-label'?: never }
  | { label?: never; 'aria-label': string };

export type RequireLabelWithOptionalHelpTooltipOrAria = RequireLabelOrAria & {
  helpTooltipProps?: HelpTooltipProps;
};

/**
 * Forwards a `label`/`aria-label` pair to a child that requires one. Destructuring the pair out of
 * a prop bag splits the union, so it has to be reassembled — and the cast that costs us is checked
 * at runtime here, loudly in development and non-fatally in production.
 */
export function getLabelOrAriaProps(props: {
  label?: ReactNode;
  'aria-label'?: string;
}): RequireLabelOrAria {
  const label = props.label;
  const ariaLabel = props['aria-label'];
  const hasStringLabel = typeof label === 'string' && isTruthy(label);
  if (!isTruthy(ariaLabel) && !hasStringLabel) {
    const message = isDefined(label)
      ? 'A non-string `label` carries no accessible name, so `aria-label` is required'
      : 'One of `label` or `aria-label` is required';
    if (process.env.NODE_ENV === 'production') {
      console.warn(message, props);
    } else {
      throw new Error(message);
    }
  }
  return { label, 'aria-label': ariaLabel } as RequireLabelOrAria;
}
